import React, { useState, useEffect, useRef } from 'react';
import { css, keyframes } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../../../styles';
import bloomLogo from '../../../../assets/Icons/bloom_logo.svg';

interface PortfolioAnimationProps {
  isActive: boolean;
}

const HOLDINGS = [
  { symbol: 'AAPL', shares: '270 shares', change: '+2.4%', equity: '$51K' },
  { symbol: 'NVDA', shares: '35 shares', change: '+1.8%', equity: '$30K' },
  { symbol: 'GOOGL', shares: '145 shares', change: '+1.2%', equity: '$25K' },
];

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const PortfolioAnimation: React.FC<PortfolioAnimationProps> = ({ isActive }) => {
  const [phase, setPhase] = useState<'empty' | 'loading' | 'holdings' | 'alert'>('empty');
  const [visibleHoldings, setVisibleHoldings] = useState<number[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    // Clear any existing timeouts
    timeoutIdsRef.current.forEach((id) => clearTimeout(id));
    timeoutIdsRef.current = [];

    if (isActive) {
      setPhase('empty');
      setVisibleHoldings([]);
      setShowAlert(false);

      // Phase transitions
      timeoutIdsRef.current.push(setTimeout(() => setPhase('loading'), 400));
      timeoutIdsRef.current.push(setTimeout(() => setPhase('holdings'), 1000));
      timeoutIdsRef.current.push(setTimeout(() => setPhase('alert'), 2400));

      // Stagger holdings appearance
      HOLDINGS.forEach((_, index) => {
        const id = setTimeout(
          () => {
            setVisibleHoldings((prev) => [...prev, index]);
          },
          1200 + index * 300,
        );
        timeoutIdsRef.current.push(id);
      });

      // Show alert notification
      timeoutIdsRef.current.push(setTimeout(() => setShowAlert(true), 2600));
    }

    return () => {
      const ids = timeoutIdsRef.current;
      ids.forEach((id) => clearTimeout(id));
    };
  }, [isActive]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: ${spacing.deci};
        height: 100%;
        overflow: hidden;
      `}
    >
      {/* Empty state */}
      {phase === 'empty' && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            gap: ${spacing.deci};
          `}
        >
          <div
            css={css`
              ${fontStyles.cat};
              color: ${colors.tungsten};
              text-align: center;

              html[data-theme='dark'] & {
                color: ${colors.silver};
              }
            `}
          >
            Connect Your Portfolio
          </div>
          <div
            css={css`
              display: flex;
              gap: ${spacing.centi};
            `}
          >
            {['🏦', '🏦', '🏦'].map((icon, i) => (
              <div
                key={i}
                css={css`
                  width: 40px;
                  height: 40px;
                  background: ${colors.sugar};
                  border-radius: 8px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 20px;

                  html[data-theme='dark'] & {
                    background: ${colors.liquorice};
                  }
                `}
              >
                {icon}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading state */}
      {phase === 'loading' && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            gap: ${spacing.deci};
          `}
        >
          <div
            css={css`
              width: 32px;
              height: 32px;
              border: 3px solid ${colors.cloud};
              border-top-color: ${colors.petrolBlue};
              border-radius: 50%;
              animation: ${spin} 1s linear infinite;

              html[data-theme='dark'] & {
                border-color: ${colors.liquorice};
                border-top-color: ${colors.skyBlue};
              }
            `}
          />
          <div
            css={css`
              ${fontStyles.cat};
              color: ${colors.tungsten};

              html[data-theme='dark'] & {
                color: ${colors.silver};
              }
            `}
          >
            Syncing your portfolio...
          </div>
        </div>
      )}

      {/* Holdings state */}
      {(phase === 'holdings' || phase === 'alert') && (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            gap: 6px;
            flex: 1;
            overflow: hidden;
          `}
        >
          {/* Holdings list */}
          {HOLDINGS.map((holding, index) => {
            const isVisible = visibleHoldings.includes(index);

            return (
              <div
                key={holding.symbol}
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  padding: 6px ${spacing.centi};
                  background: ${colors.sugar};
                  border-radius: 8px;
                  opacity: ${isVisible ? 1 : 0};
                  transform: translateY(${isVisible ? (showAlert ? -4 : 0) : 10}px);
                  transition:
                    opacity 0.3s ease-out,
                    transform 0.3s ease-out;

                  html[data-theme='dark'] & {
                    background: ${colors.liquorice};
                  }
                `}
              >
                <div>
                  <div
                    css={css`
                      ${fontStyles.fatCat};
                      color: ${colors.liquorice};

                      html[data-theme='dark'] & {
                        color: ${colors.sugar};
                      }
                    `}
                  >
                    {holding.symbol}
                  </div>
                  <div
                    css={css`
                      ${fontStyles.flea};
                      color: ${colors.tungsten};

                      html[data-theme='dark'] & {
                        color: ${colors.silver};
                      }
                    `}
                  >
                    {holding.shares}
                  </div>
                </div>
                <div
                  css={css`
                    text-align: right;
                  `}
                >
                  <div
                    css={css`
                      ${fontStyles.fatCat};
                      color: ${colors.liquorice};

                      html[data-theme='dark'] & {
                        color: ${colors.sugar};
                      }
                    `}
                  >
                    {holding.equity}
                  </div>
                  <div
                    css={css`
                      ${fontStyles.flea};
                      color: ${colors.leaf};
                    `}
                  >
                    {holding.change}
                  </div>
                </div>
              </div>
            );
          })}

          {/* iOS-style notification - fades in at bottom */}
          {showAlert && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 10px 12px;
                background: rgba(255, 255, 255, 0.95);
                backdrop-filter: blur(20px);
                border-radius: 16px;
                animation: ${fadeIn} 0.4s ease-out forwards;

                html[data-theme='dark'] & {
                  background: rgba(50, 50, 50, 0.95);
                }
              `}
            >
              {/* App icon */}
              <img
                src={bloomLogo}
                alt="Bloom"
                css={css`
                  width: 20px;
                  height: 20px;
                  flex-shrink: 0;
                `}
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 1px;
                  min-width: 0;
                  flex: 1;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: ${spacing.centi};
                  `}
                >
                  <span
                    css={css`
                      ${fontStyles.fatCat};
                      color: ${colors.liquorice};

                      html[data-theme='dark'] & {
                        color: ${colors.sugar};
                      }
                    `}
                  >
                    NVDA up 4.2% after hours
                  </span>
                  <span
                    css={css`
                      ${fontStyles.flea};
                      color: ${colors.cloud};

                      html[data-theme='dark'] & {
                        color: ${colors.tungsten};
                      }
                    `}
                  >
                    now
                  </span>
                </div>
                <div
                  css={css`
                    ${fontStyles.mouse};
                    color: ${colors.tungsten};

                    html[data-theme='dark'] & {
                      color: ${colors.silver};
                    }
                  `}
                >
                  Consider rebalancing.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
